import first from "lodash/first";
import { useCallback, useMemo } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";
import CreateSeedRandom from "seed-random";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";
import { isAfterOrEqual, isBeforeOrEqual } from "shared/lib/utils/dates";

import type { PWAAdvertCampaignAppConfig } from "scmp-app/components/section/default-section/types";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { hooksHomeV2PickMorningStudioArticle$key } from "scmp-app/queries/__generated__/hooksHomeV2PickMorningStudioArticle.graphql";
import type { hooksPremiumNativeAdConfigQuery$key } from "scmp-app/queries/__generated__/hooksPremiumNativeAdConfigQuery.graphql";
import type { hooksTopStoriesTrackingQueueItemsEdge$key } from "scmp-app/queries/__generated__/hooksTopStoriesTrackingQueueItemsEdge.graphql";

export const usePickMorningStudioArticles = (
  seed: string,
  reference?: hooksHomeV2PickMorningStudioArticle$key | null,
) =>
  useMemo(() => {
    const random = CreateSeedRandom(seed);
    const data = readInlineData(
      graphql`
        fragment hooksHomeV2PickMorningStudioArticle on Queue @inline {
          # keep in sync with HOME_V2_MORNING_STUDIO_NATIVE_AD_ITEM_COUNT (components/home-v2/constants.ts)
          items(first: 4) {
            edges {
              # eslint-disable-next-line relay/unused-fields
              node {
                ...homeNativeArticleContentItemContent
              }
            }
          }
        }
      `,
      reference ?? null,
    );

    const slot1Articles = (data?.items?.edges ?? []).slice(0, 2);
    const slot2Articles = (data?.items?.edges ?? []).slice(2, 4);
    const slot1Picked = slot1Articles.length > 0 ? Math.floor(random() * slot1Articles.length) : 0;
    const slot2Picked = slot2Articles.length > 0 ? Math.floor(random() * slot2Articles.length) : 0;

    return {
      slot1: slot1Articles[slot1Picked] ?? null,
      slot2: slot2Articles[slot2Picked] ?? null,
    };
  }, [seed, reference]);

export const usePremiumNativeAd = (reference: hooksPremiumNativeAdConfigQuery$key) => {
  const data = useFragment(
    graphql`
      fragment hooksPremiumNativeAdConfigQuery on Query {
        advertConfig: appConfig(filter: { entityId: "scmp_pwa_advert_campaign" }) {
          json
        }
      }
    `,
    reference,
  );

  const advertConfig = (data?.advertConfig?.json ?? {}) as PWAAdvertCampaignAppConfig;
  const homepageConfig = advertConfig?.premiumNativeAd?.homepage;
  const currentTime = new Date();
  const startDateTime = new Date(homepageConfig?.startDateTime ?? 0);
  const endDateTime = new Date(homepageConfig?.endDateTime ?? 0);
  const isEnableByDateTime =
    homepageConfig?.enableByDateTime &&
    isAfterOrEqual(currentTime, startDateTime) &&
    isBeforeOrEqual(currentTime, endDateTime);

  return homepageConfig?.alwaysEnable || isEnableByDateTime;
};

export const useTopStoriesTracking = (reference: hooksTopStoriesTrackingQueueItemsEdge$key) => {
  const data = useFragment(
    graphql`
      fragment hooksTopStoriesTrackingQueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            urlAlias
          }
        }
      }
    `,
    reference,
  );
  const articleIds = useMemo(() => data.map(edge => edge.node.entityId).filter(notEmpty), [data]);
  const urlAlias = useMemo(() => data.map(edge => edge.node.urlAlias).filter(notEmpty), [data]);

  const getWidgetName = (position: number) => {
    if (position < 3) return "top_story1";
    if (position < 7) return "top_story4";
    if (position < 11) return "top_story8";
    if (position < 15) return "top_story12";
    return "top_story16";
  };

  const sendGA4SponsorClickEvent = useCallback((entityId?: string, urlAlias?: string) => {
    if (!notEmpty(entityId) || !notEmpty(urlAlias)) return;
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        destination_url: getAbsoluteUrl(urlAlias),
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    });
  }, []);

  const sendGA4ClickEvent = useCallback(
    (entityId?: string) => {
      const position = articleIds.indexOf(entityId ?? "");
      if (position === -1 || position >= urlAlias.length) return;

      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: {
          article_id: entityId,
          article_seq: `${position + 1}`,
          destination_url: getAbsoluteUrl(urlAlias[position]),
          page_type: "homepage",
          widget_name: getWidgetName(position),
        },
        subcategory: "widget",
      });
    },
    [articleIds, urlAlias],
  );

  const getGA4ImpressionEvent = useCallback(
    (entityIds: string[]) => () => {
      const firstEntityId = first(entityIds) ?? "";
      const position = articleIds.indexOf(firstEntityId);
      if (position === -1) return;

      return {
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: entityIds.join(","),
          page_type: "homepage",
          widget_name: getWidgetName(position),
        },
        subcategory: "widget",
      } satisfies RecirculationWidgetImpressionEvent;
    },
    [articleIds],
  );

  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement1 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getGA4ImpressionEvent(articleIds.slice(0, 3)),
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement2 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getGA4ImpressionEvent(articleIds.slice(3, 7)),
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement3 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getGA4ImpressionEvent(articleIds.slice(7, 11)),
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement4 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getGA4ImpressionEvent(articleIds.slice(11, 15)),
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement5 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getGA4ImpressionEvent(articleIds.slice(15)),
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return {
    captureTrackImpressionEventElement1,
    captureTrackImpressionEventElement2,
    captureTrackImpressionEventElement3,
    captureTrackImpressionEventElement4,
    captureTrackImpressionEventElement5,
    sendGA4ClickEvent,
    sendGA4SponsorClickEvent,
  };
};

export const useTopStoriesNativeAdImpressionTracking = () => {
  const nativeAdImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: nativeAdImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });
  return captureTrackImpressionEventTargetElement;
};
